// ============================================
// ProgressBar Component
// Horizontal progress bar with color variants
// ============================================

import { cn } from '../../utils/cn';

// ============================================
// Types
// ============================================

type ProgressVariant = 'healthy' | 'warning' | 'critical';

interface ProgressBarProps {
  /** Current value */
  value: number;
  /** Maximum value */
  max: number;
  /** Color variant */
  variant?: ProgressVariant;
  /** Auto-determine variant based on percentage */
  autoVariant?: boolean;
  /** Height in pixels */
  height?: number;
  /** Show percentage label */
  showLabel?: boolean;
  /** Additional className */
  className?: string;
}

// ============================================
// Styles
// ============================================

const variantStyles: Record<ProgressVariant, string> = {
  healthy: 'bg-[#004AC6]',
  warning: 'bg-amber-500',
  critical: 'bg-[#BA1A1A]',
};

// ============================================
// Component
// ============================================

export function ProgressBar({
  value,
  max,
  variant,
  autoVariant = false,
  height = 6,
  showLabel = false,
  className,
}: ProgressBarProps) {
  const percentage = max > 0 ? Math.min(100, (value / max) * 100) : 0;

  // Auto-determine variant based on percentage if enabled
  let resolvedVariant = variant || 'healthy';
  if (autoVariant && !variant) {
    if (percentage < 20) {
      resolvedVariant = 'critical';
    } else if (percentage < 40) {
      resolvedVariant = 'warning';
    } else {
      resolvedVariant = 'healthy';
    }
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Progress track */}
      <div
        className="flex-1 overflow-hidden rounded-full bg-slate-100"
        style={{ height }}
      >
        {/* Progress fill */}
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            variantStyles[resolvedVariant]
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Label */}
      {showLabel && (
        <span className="text-xs font-medium text-slate-500">
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
}

// ============================================
// Stock Progress Bar
// Specialized for inventory stock display
// ============================================

interface StockProgressBarProps {
  /** Current available quantity */
  available: number;
  /** Total/maximum quantity */
  total: number;
  /** Low stock threshold (percentage) */
  lowStockThreshold?: number;
  /** Additional className */
  className?: string;
}

export function StockProgressBar({
  available,
  total,
  lowStockThreshold = 20,
  className,
}: StockProgressBarProps) {
  const percentage = total > 0 ? (available / total) * 100 : 0;
  const isLowStock = percentage < lowStockThreshold;

  return (
    <ProgressBar
      value={available}
      max={total}
      variant={isLowStock ? 'critical' : 'healthy'}
      height={6}
      className={className}
    />
  );
}
